import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  deleteAllSuppliers,
  createBlankSupplier,
  addSupplierLocally,
  removeSupplierLocally
} from '../store/slices/suppliersSlice';
import { SupplierCard } from '../features/suppliers/components/SupplierCard';
import { Supplier } from '../types/supplier';

export const Suppliers: React.FC = () => {
  const dispatch = useAppDispatch();
  const suppliers = useAppSelector(state => state.suppliers.suppliers);
  const loading = useAppSelector(state => state.suppliers.loading);
  const error = useAppSelector(state => state.suppliers.error);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Accordion state - only one supplier can be expanded at a time
  const [expandedSupplierId, setExpandedSupplierId] = useState<string | null>(null);
  const [requestedSupplierId, setRequestedSupplierId] = useState<string | null>(null);
  const unsavedChangesCheckRef = useRef<(() => boolean) | null>(null);

  // Track new suppliers that haven't been saved to the backend yet
  const [newSupplierIds, setNewSupplierIds] = useState<Set<string>>(new Set());

  // Get the target supplier ID from URL
  const targetSupplierId = searchParams.get('open');

  // Fetch suppliers from API on mount
  useEffect(() => {
    dispatch(fetchSuppliers());
  }, [dispatch]);

  const handleAddSupplier = () => {
    const newSupplier = createBlankSupplier();
    // Add to local state only (don't POST to backend yet)
    dispatch(addSupplierLocally(newSupplier));
    // Track as new supplier
    setNewSupplierIds(prev => new Set(prev).add(newSupplier.id));
    // Auto-expand the new supplier
    setExpandedSupplierId(newSupplier.id);
  };

  const handleCreateSupplier = async (supplier: Supplier) => {
    const clientId = supplier.id;
    // POST to backend to create the supplier
    const result = await dispatch(createSupplier(supplier));

    // If successful, remove from new suppliers set and close the form
    if (createSupplier.fulfilled.match(result)) {
      setNewSupplierIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(clientId);
        return newSet;
      });
      // Close the form after successful save
      setExpandedSupplierId(null);
    }
  };

  const handleUpdateSupplier = async (updatedSupplier: Supplier) => {
    await dispatch(updateSupplier(updatedSupplier));
  };

  const handleDeleteSupplier = async (id: string) => {
    // Check if this is a new supplier that hasn't been saved yet
    if (newSupplierIds.has(id)) {
      // Just remove from local state, no API call needed
      dispatch(removeSupplierLocally(id));
      setNewSupplierIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } else {
      // Existing supplier, call API to delete
      await dispatch(deleteSupplier(id));
    }
    // Clear expansion if deleted supplier was expanded
    if (expandedSupplierId === id) {
      setExpandedSupplierId(null);
    }
  };

  const handleDeleteAllSuppliers = async () => {
    if (suppliers.length === 0) {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete all ${suppliers.length} supplier${suppliers.length !== 1 ? 's' : ''}? This action cannot be undone.`
    );

    if (confirmed) {
      await dispatch(deleteAllSuppliers());
    }
  };

  const handleSupplierToggle = (supplierId: string) => {
    // Collapse if clicking same supplier
    if (expandedSupplierId === supplierId) {
      setExpandedSupplierId(null);
      return;
    }

    // Check for unsaved changes before switching
    if (expandedSupplierId && unsavedChangesCheckRef.current?.()) {
      setRequestedSupplierId(supplierId);
      return;
    }

    setExpandedSupplierId(supplierId);
  };

  const handleUnsavedChangesCheck = (checkFn: () => boolean) => {
    unsavedChangesCheckRef.current = checkFn;
  };

  const handleConfirmSwitch = () => {
    if (requestedSupplierId) {
      setExpandedSupplierId(requestedSupplierId);
      setRequestedSupplierId(null);
    }
  };

  const handleCancelSwitch = () => {
    setRequestedSupplierId(null);
  };

  // Clear search and URL parameter when navigating to a specific supplier
  useEffect(() => {
    if (targetSupplierId) {
      // Clear search to ensure supplier is visible
      setSearchQuery('');

      // Expand the target supplier
      setExpandedSupplierId(targetSupplierId);

      // Clear URL parameter after a brief delay
      const timer = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [targetSupplierId, setSearchParams]);

  // Filter suppliers based on search query
  const filteredSuppliers = suppliers.filter((supplier: Supplier) => {
    const query = searchQuery.toLowerCase();
    if (!query) {
      return true;
    }

    // Check if any ingredient matches the search query
    const ingredientMatch = supplier.ingredientsSupplied.some(ingredient => ingredient.toLowerCase().includes(query));

    return (
      supplier.supplierName.toLowerCase().includes(query) ||
      supplier.contactInformation.toLowerCase().includes(query) ||
      (supplier.contactName && supplier.contactName.toLowerCase().includes(query)) ||
      (supplier.contactEmail && supplier.contactEmail.toLowerCase().includes(query)) ||
      (supplier.contactPhone && supplier.contactPhone.toLowerCase().includes(query)) ||
      ingredientMatch ||
      supplier.approvedBy.toLowerCase().includes(query)
    );
  });

  // Sort suppliers alphabetically by name
  const sortedSuppliers = [...filteredSuppliers].sort((a, b) => {
    const nameA = a.supplierName.toLowerCase() || 'zzz';
    const nameB = b.supplierName.toLowerCase() || 'zzz';
    return nameA.localeCompare(nameB);
  });

  // Auto-collapse supplier if filtered out by search
  useEffect(() => {
    if (expandedSupplierId) {
      const isVisible = sortedSuppliers.some(s => s.id === expandedSupplierId);
      if (!isVisible) {
        setExpandedSupplierId(null);
      }
    }
  }, [expandedSupplierId, sortedSuppliers]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden'
      }}
    >
      {/* Sticky Header Section */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <header
            style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Suppliers</h1>
              <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>
                {suppliers.length} supplier{suppliers.length !== 1 ? 's' : ''} total
                {searchQuery && ` • ${sortedSuppliers.length} matching search`}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleDeleteAllSuppliers}
                disabled={suppliers.length === 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: suppliers.length === 0 ? '#ccc' : '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: suppliers.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                Delete All Suppliers
              </button>
              <button
                onClick={handleAddSupplier}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                + Add New Supplier
              </button>
            </div>
          </header>

          {/* Search Bar */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search suppliers by name, contact, or ingredients..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontSize: '1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                outline: 'none'
              }}
            />
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f8fafc'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Error Display */}
          {error && (
            <div
              style={{
                padding: '16px',
                marginBottom: '20px',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c33',
                fontSize: '1rem'
              }}
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Loading Spinner */}
          {loading ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px'
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  width: '48px',
                  height: '48px',
                  border: '5px solid #e0e0e0',
                  borderTop: '5px solid #4CAF50',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
              <p style={{ marginTop: '16px', color: '#666', fontSize: '1.1rem' }}>Loading suppliers...</p>
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          ) : sortedSuppliers.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '60px 20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                color: '#999'
              }}
            >
              {searchQuery ? (
                <p style={{ fontSize: '1.2rem', margin: 0 }}>No suppliers match your search</p>
              ) : (
                <>
                  <p style={{ fontSize: '1.2rem', margin: '0 0 16px 0' }}>No suppliers yet</p>
                  <button
                    onClick={handleAddSupplier}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    + Add Your First Supplier
                  </button>
                </>
              )}
            </div>
          ) : (
            sortedSuppliers.map(supplier => (
              <SupplierCard
                key={supplier.id}
                supplier={supplier}
                isNew={newSupplierIds.has(supplier.id)}
                onCreate={handleCreateSupplier}
                onUpdate={handleUpdateSupplier}
                onDelete={handleDeleteSupplier}
                isExpanded={supplier.id === expandedSupplierId}
                onToggleExpand={handleSupplierToggle}
                onRegisterUnsavedCheck={handleUnsavedChangesCheck}
                requestedSupplierId={supplier.id === expandedSupplierId ? requestedSupplierId : null}
                onConfirmSwitch={handleConfirmSwitch}
                onCancelSwitch={handleCancelSwitch}
                highlightOnMount={supplier.id === targetSupplierId}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
